// src/components/ApprovalDashboard.tsx
import React from 'react';
import {
  Box, Grid, Typography, Card, CardContent, Chip, Avatar,
  Stack, Table, TableHead, TableBody, TableRow, TableCell,
  IconButton, Tooltip, Divider,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Group as GroupIcon,
  Place as LocationIcon,
  AssignmentInd as AssignIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import type { VehicleRequest } from '../types';

interface ApprovalDashboardProps {
  requests: VehicleRequest[];
  onApprove: (id: string, type: 'supervisor' | 'fleet') => void;
  onReject: (id: string, type: 'supervisor' | 'fleet') => void;
  userRole?: 'supervisor' | 'fleet_manager' | 'company_admin';
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Safely get requester display name — works for both internal users and public requests
const getRequesterName = (request: any): string => {
  // Public portal request — has requesterName directly on the document
  if (request.isPublicRequest && request.requesterName) {
    return request.requesterName;
  }
  // Internal request — requestedBy is a populated User object
  if (request.requestedBy && typeof request.requestedBy === 'object') {
    const { firstName, lastName } = request.requestedBy as any;
    if (firstName) return `${firstName} ${lastName || ''}`.trim();
  }
  return 'Unknown';
};

const getRequesterDepartment = (request: any): string | null => {
  if (request.isPublicRequest) return request.requesterDepartment || null;
  const rb = request.requestedBy as any;
  return rb?.department || null;
};

const getAvatarLetter = (request: any): string => {
  const name = getRequesterName(request);
  return name.charAt(0).toUpperCase() || '?';
};

const getPriorityColor = (priority: string): any => {
  switch (priority) {
    case 'urgent':
    case 'high': return 'error';
    case 'medium': return 'warning';
    case 'low': return 'success';
    default: return 'default';
  }
};

const getStatusChip = (status: string) => {
  switch (status) {
    case 'pending':
      return <Chip size="small" label="Pending Approval" color="warning" variant="outlined" />;
    case 'supervisor_approved':
      return <Chip size="small" label="Pending Vehicle Assignment" color="info" variant="outlined" />;
    case 'fleet_approved':
      return <Chip size="small" label="Approved" color="success" />;
    case 'rejected':
      return <Chip size="small" label="Rejected" color="error" />;
    case 'completed':
      return <Chip size="small" label="Completed" color="success" />;
    case 'in_progress':
      return <Chip size="small" label="In Progress" color="primary" />;
    default:
      return <Chip size="small" label={status} color="default" />;
  }
};

// ── Requester cell — shared between both tables ───────────────────────────────
const RequesterCell: React.FC<{ request: any }> = ({ request }) => {
  const name = getRequesterName(request);
  const department = getRequesterDepartment(request);

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Avatar sx={{ width: 32, height: 32, bgcolor: request.isPublicRequest ? 'secondary.main' : 'primary.main' }}>
        {getAvatarLetter(request)}
      </Avatar>
      <Box>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body2" fontWeight="bold">{name}</Typography>
          {request.isPublicRequest && (
            <Tooltip title="Public portal request">
              <PublicIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
            </Tooltip>
          )}
        </Stack>
        {department && (
          <Typography variant="caption" color="text.secondary">{department}</Typography>
        )}
        {request.isPublicRequest && request.referenceNumber && (
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {request.referenceNumber}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const ApprovalDashboard: React.FC<ApprovalDashboardProps> = ({
  requests, onApprove, onReject, userRole = 'fleet_manager'
}) => {
  const pendingSupervisorRequests = requests.filter(r => r.status === 'pending');
  const pendingFleetRequests = requests.filter(r => r.status === 'supervisor_approved');

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Approval Dashboard
      </Typography>

      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Pending Initial Approval
              </Typography>
              <Typography variant="h4">{pendingSupervisorRequests.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Pending Vehicle Assignment
              </Typography>
              <Typography variant="h4">{pendingFleetRequests.length}</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Pending Initial Approval */}
        {pendingSupervisorRequests.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Pending Initial Approval</Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ width: '100%', overflowX: 'auto' }}><Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Priority</TableCell>
                      <TableCell>Requester</TableCell>
                      <TableCell>Trip Details</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingSupervisorRequests.map((request: any) => (
                      <TableRow key={request._id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                        <TableCell>
                          <Chip size="small" color={getPriorityColor(request.priority)} label={request.priority} variant="filled" />
                        </TableCell>
                        <TableCell>
                          <RequesterCell request={request} />
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200, fontWeight: 'bold' }}>
                              {request.purpose}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <LocationIcon fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary">{request.destination}</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <GroupIcon fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary">{request.passengerCount} passengers</Typography>
                            </Stack>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {format(new Date(request.startDateTime), 'MMM d, yyyy')}
                          </Typography>
                        </TableCell>
                        <TableCell>{getStatusChip(request.status)}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Approve Request">
                              <IconButton color="success" onClick={() => onApprove(request._id, 'supervisor')}>
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject Request">
                              <IconButton color="error" onClick={() => onReject(request._id, 'supervisor')}>
                                <RejectIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table></Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Pending Vehicle Assignment */}
        {pendingFleetRequests.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Pending Vehicle Assignment</Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ width: '100%', overflowX: 'auto' }}><Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Priority</TableCell>
                      <TableCell>Requester</TableCell>
                      <TableCell>Trip Details</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingFleetRequests.map((request: any) => (
                      <TableRow key={request._id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                        <TableCell>
                          <Chip size="small" color={getPriorityColor(request.priority)} label={request.priority} variant="filled" />
                        </TableCell>
                        <TableCell>
                          <RequesterCell request={request} />
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200, fontWeight: 'bold' }}>
                              {request.purpose}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <LocationIcon fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary">{request.destination}</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <GroupIcon fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary">{request.passengerCount} passengers</Typography>
                            </Stack>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {format(new Date(request.startDateTime), 'MMM d, yyyy')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {request.vehicleCategory && (
                            <Chip size="small" label={request.vehicleCategory} variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>{getStatusChip(request.status)}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Assign Vehicle">
                              <IconButton color="primary" onClick={() => onApprove(request._id, 'fleet')}>
                                <AssignIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject Request">
                              <IconButton color="error" onClick={() => onReject(request._id, 'fleet')}>
                                <RejectIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table></Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Empty State */}
        {pendingSupervisorRequests.length === 0 && pendingFleetRequests.length === 0 && (
          <Grid item xs={12}>
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>No Pending Approvals</Typography>
              <Typography variant="body2" color="text.secondary">All vehicle requests have been processed</Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ApprovalDashboard;